import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useMercenary } from '../../../contexts/MercenaryContext';
import Toast from '../../Shared/Toast';
import './ActiveOrders.css';

interface Order {
    id: number;
    itemName: string;
    price: number;
    quantity: number;
    timeLeft: string;
    hoursLeft: number;
    type: 'buy' | 'sell';
}

const ActiveOrders: React.FC = () => {
    const { mercenary } = useMercenary();
    const [buyOrders, setBuyOrders] = useState<Order[]>([]);
    const [sellOrders, setSellOrders] = useState<Order[]>([]);
    const [cancellingId, setCancellingId] = useState<number | null>(null);
    const [toastMessage, setToastMessage] = useState<string | null>(null);
    const [toastType, setToastType] = useState<'success' | 'error'>('success');

    useEffect(() => {
        if (mercenary?.id) {
            axios.get(`/api/auction/orders/active/${mercenary.id}`)
                .then(response => {
                    const { buyOrders, sellOrders } = response.data;
                    setBuyOrders(buyOrders.map((order: any) => parseOrder(order, 'buy')));
                    setSellOrders(sellOrders.map((order: any) => parseOrder(order, 'sell')));
                })
                .catch(() => displayToast('Error fetching active orders', 'error'));
        }
    }, [mercenary]);

    const parseOrder = (order: any, type: 'buy' | 'sell'): Order => {
        const diff = new Date(order.expires_at).getTime() - Date.now();
        const hours = Math.max(0, Math.floor(diff / (1000 * 60 * 60)));
        const minutes = Math.max(0, Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60)));
        return {
            id: order.id,
            itemName: order.Item.name,
            price: type === 'buy' ? order.price_offer : order.price,
            quantity: order.quantity,
            timeLeft: `${hours}h ${minutes}m`,
            hoursLeft: hours,
            type,
        };
    };

    const getTimeClass = (hours: number) => {
        if (hours < 6) return 'ao-time-urgent';
        if (hours < 24) return 'ao-time-soon';
        return 'ao-time-plenty';
    };

    const cancelOrder = async (orderId: number, orderType: 'buy' | 'sell') => {
        setCancellingId(orderId);
        try {
            const response = await axios.post('/api/auction/orders/cancel', {
                orderId,
                orderType,
                mercenaryId: mercenary?.id,
            });
            displayToast(response.data.message, 'success');
            if (orderType === 'buy') {
                setBuyOrders(prev => prev.filter(o => o.id !== orderId));
            } else {
                setSellOrders(prev => prev.filter(o => o.id !== orderId));
            }
        } catch (error: any) {
            displayToast(error.response?.data.error || 'Failed to cancel order', 'error');
        } finally {
            setCancellingId(null);
        }
    };

    const displayToast = (message: string, type: 'success' | 'error') => {
        setToastMessage(message);
        setToastType(type);
    };

    const renderTable = (orders: Order[], type: 'buy' | 'sell') => (
        <table className="ao-table">
            <thead>
                <tr>
                    <th>Item</th>
                    <th>Qty</th>
                    <th>{type === 'buy' ? 'Offer' : 'Price'}</th>
                    <th>Expires</th>
                    <th></th>
                </tr>
            </thead>
            <tbody>
                {orders.map(order => (
                    <tr key={order.id}>
                        <td className="ao-cell-name">{order.itemName}</td>
                        <td className="ao-cell-qty">{order.quantity}</td>
                        <td className="ao-cell-price">{order.price} silver</td>
                        <td className={`ao-cell-time ${getTimeClass(order.hoursLeft)}`}>
                            {order.timeLeft}
                        </td>
                        <td className="ao-cell-action">
                            <button
                                className="ao-cancel-btn"
                                disabled={cancellingId === order.id}
                                onClick={() => cancelOrder(order.id, type)}
                                title="Cancel order"
                            >
                                {cancellingId === order.id ? '…' : '✕'}
                            </button>
                        </td>
                    </tr>
                ))}
            </tbody>
        </table>
    );

    return (
        <div className="ao-container">
            {toastMessage && (
                <Toast
                    message={toastMessage}
                    type={toastType}
                    duration={3000}
                    onClose={() => setToastMessage(null)}
                />
            )}

            <div className="ao-section">
                <h3>💰 Active Buy Orders</h3>
                {buyOrders.length > 0 ? (
                    <div className="ao-scroll">{renderTable(buyOrders, 'buy')}</div>
                ) : (
                    <p className="ao-empty">No active buy orders</p>
                )}
            </div>

            <div className="ao-section">
                <h3>📜 Active Sell Orders</h3>
                {sellOrders.length > 0 ? (
                    <div className="ao-scroll">{renderTable(sellOrders, 'sell')}</div>
                ) : (
                    <p className="ao-empty">No active sell orders</p>
                )}
            </div>
        </div>
    );
};

export default ActiveOrders;
